import { useState } from 'react';

interface ConceptMapProps {
  onAgeSelect: (ageGroup: string) => void;
  onCategorySelect: (category: string) => void;
  selectedAge: string | null;
  selectedCategory: string | null;
}

const ageGroups = [
  { id: 'preschool', label: 'Preschool', subLabel: 'ages 3–4', color: 'bg-[#E91E8C]', hoverColor: 'hover:bg-[#D91E8C]' },
  { id: 'early-elementary', label: 'Early Elementary', subLabel: '5–7', color: 'bg-[#F5C518]', hoverColor: 'hover:bg-[#E5B518]' },
  { id: 'upper-elementary', label: 'Upper Elementary', subLabel: '8–10', color: 'bg-[#D946A6]', hoverColor: 'hover:bg-[#C946A6]' },
  { id: 'tweens', label: 'Tweens', subLabel: '11–13', color: 'bg-[#3D1A6E]', hoverColor: 'hover:bg-[#2D1A5E]' },
  { id: 'teens', label: 'Teens', subLabel: '14–17', color: 'bg-[#F59E0B]', hoverColor: 'hover:bg-[#E59E0B]' }
];

const categories = [
  { id: 'sports', label: 'Sports', icon: 'ri-basketball-line', color: 'bg-[#E91E8C]', hoverColor: 'hover:bg-[#D91E8C]' },
  { id: 'stem', label: 'STEM / Tech', icon: 'ri-code-line', color: 'bg-[#3D1A6E]', hoverColor: 'hover:bg-[#2D1A5E]' },
  { id: 'arts', label: 'Arts / Creative', icon: 'ri-palette-line', color: 'bg-[#D946A6]', hoverColor: 'hover:bg-[#C946A6]' },
  { id: 'outdoor', label: 'Outdoor / Nature', icon: 'ri-leaf-line', color: 'bg-[#10B981]', hoverColor: 'hover:bg-[#059669]' },
  { id: 'faith', label: 'Faith-based', icon: 'ri-heart-line', color: 'bg-[#E91E8C]', hoverColor: 'hover:bg-[#D91E8C]' },
  { id: 'special-needs', label: 'Special Needs', icon: 'ri-hand-heart-line', color: 'bg-[#14B8A6]', hoverColor: 'hover:bg-[#0D9488]' },
  { id: 'full-day', label: 'Full-Day Camps', icon: 'ri-sun-line', color: 'bg-[#F5C518]', hoverColor: 'hover:bg-[#E5B518]' },
  { id: 'half-day', label: 'Half-Day Camps', icon: 'ri-time-line', color: 'bg-[#F59E0B]', hoverColor: 'hover:bg-[#E59E0B]' },
  { id: 'one-week', label: 'One-Week Programs', icon: 'ri-calendar-line', color: 'bg-[#3D1A6E]', hoverColor: 'hover:bg-[#2D1A5E]' },
  { id: 'overnight', label: 'Overnight Camps', icon: 'ri-moon-line', color: 'bg-[#D946A6]', hoverColor: 'hover:bg-[#C946A6]' }
];

export default function ConceptMap({ onAgeSelect, onCategorySelect, selectedAge, selectedCategory }: ConceptMapProps) {
  const [hoveredBubble, setHoveredBubble] = useState<string | null>(null);

  if (selectedAge && !selectedCategory) {
    return (
      <div className="relative w-full h-[600px] flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Center node */}
            <div className="relative z-10 w-56 h-56 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F5C518] shadow-2xl flex flex-col items-center justify-center text-white cursor-pointer transform transition-transform hover:scale-105"
                 onClick={() => onAgeSelect('')}>
              <i className="ri-arrow-left-line text-3xl mb-2"></i>
              <div className="text-lg font-bold text-center px-4">Choose Category</div>
              <div className="text-sm opacity-90 mt-1">Click to go back</div>
            </div>

            {/* Category bubbles in circular layout */}
            {categories.map((category, index) => {
              const angle = (index * 360) / categories.length;
              const radius = 280;
              const x = Math.cos((angle * Math.PI) / 180) * radius;
              const y = Math.sin((angle * Math.PI) / 180) * radius;

              return (
                <div
                  key={category.id}
                  className={`absolute w-40 h-40 rounded-full ${category.color} ${category.hoverColor} shadow-xl flex flex-col items-center justify-center text-white cursor-pointer transform transition-all duration-300 ${hoveredBubble === category.id ? 'scale-110 z-20' : 'z-10'}`}
                  style={{
                    left: `calc(50% + ${x}px - 5rem)`,
                    top: `calc(50% + ${y}px - 5rem)`
                  }}
                  onClick={() => onCategorySelect(category.id)}
                  onMouseEnter={() => setHoveredBubble(category.id)}
                  onMouseLeave={() => setHoveredBubble(null)}
                >
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className={`${category.icon} text-4xl`}></i>
                  </div>
                  <div className="text-sm font-bold text-center px-2 mt-2 leading-tight">{category.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[600px] flex items-center justify-center">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          {/* Center node */}
          <div className="relative z-10 w-64 h-64 rounded-full bg-gradient-to-br from-[#E91E8C] via-[#D946A6] to-[#F5C518] shadow-2xl flex flex-col items-center justify-center text-white">
            <i className="ri-sun-line text-5xl mb-3"></i>
            <div className="text-2xl font-bold text-center">2026 Summer</div>
            <div className="text-2xl font-bold text-center">Camp Guide</div>
            <div className="text-sm opacity-90 mt-2">Select an age group</div>
          </div>

          {/* Age group bubbles in circular layout */}
          {ageGroups.map((group, index) => {
            const angle = (index * 360) / ageGroups.length - 90;
            const radius = 280;
            const x = Math.cos((angle * Math.PI) / 180) * radius;
            const y = Math.sin((angle * Math.PI) / 180) * radius;

            return (
              <div
                key={group.id}
                className={`absolute w-44 h-44 rounded-full ${group.color} ${group.hoverColor} shadow-xl flex flex-col items-center justify-center text-white cursor-pointer transform transition-all duration-300 ${hoveredBubble === group.id ? 'scale-110 z-20' : 'z-10'}`}
                style={{
                  left: `calc(50% + ${x}px - 5.5rem)`,
                  top: `calc(50% + ${y}px - 5.5rem)`
                }}
                onClick={() => onAgeSelect(group.id)}
                onMouseEnter={() => setHoveredBubble(group.id)}
                onMouseLeave={() => setHoveredBubble(null)}
              >
                <div className="text-lg font-bold text-center px-3">{group.label}</div>
                <div className="text-sm opacity-90 mt-1">{group.subLabel}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}